// ============================================================
// HOUSE OF CARDS — unstableEyes v1
// ------------------------------------------------------------
// Tome's sight, once it opens, never closes the same way again.
// With Unstable Eyes active the house shows what it has been
// hiding: objects that aren't in the ordinary room at all, and
// the absence of objects that were only ever a comfort.
//
// FOR NOW (per design) the ROOM itself does not re-skin — that's
// a deferred facelift. Only OBJECTS appear or vanish with the eyes,
// and any object that exists ONLY in the unstable world renders as
// a shadow-version of itself: the house's true form bleeding through
// — shadowy, biorganic, haunted, rotten. The unstable world is not
// "elsewhere"; it is THIS house, THIS ground, seen without mercy.
//
// HOTSPOT DATA CONTRACT (added to roomData hotspots, all optional):
//   eyes: "any"      → visible regardless of eyes state (default)
//   eyes: "stable"   → visible ONLY with eyes OFF (a false comfort
//                      that dissolves once Tome truly looks)
//   eyes: "unstable" → visible ONLY with eyes ON; renders as a
//                      shadow-version (no real-world counterpart)
//
// Existing hiddenUntil / removedBy flag logic is preserved and
// applied on top of the eyes gate — nothing about the old contract
// changes; this only adds a perception layer above it.
// ============================================================

// Does this hotspot belong in the room given the current eyes state?
// Mirrors the old filter's shape so it slots straight into SceneEngine.
export function eyesVisible(h, eyesActive) {
  const mode = h.eyes || "any";
  if (mode === "any") return true;
  if (mode === "stable") return !eyesActive;   // gone the moment you truly see
  if (mode === "unstable") return !!eyesActive; // only there when you do
  return true;
}

// Is this object a thing that exists ONLY in the unstable world?
// Those get the shadow-version treatment when rendered.
export function isShadowObject(h) {
  return (h.eyes || "any") === "unstable";
}

// Wrapper style/class for a hotspot's art when it's a shadow object.
// The house's true form: shadowy, biorganic, haunted, rotten. We do
// this with filters so it layers over ANY existing SVG sprite without
// needing a second hand-drawn asset per object (assets come later).
export function shadowObjectStyle() {
  return {
    // collapse the sprite toward silhouette, then bruise it with the
    // house's colors — flesh/mold bleed, not clean black.
    filter: [
      "brightness(0.16)",          // sink it into shadow
      "contrast(1.35)",            // keep an edge so the shape still reads
      "sepia(0.55)",               // warm rot underbase
      "hue-rotate(285deg)",        // push toward bruise-violet / vein
      "saturate(1.6)",
      "drop-shadow(0 0 10px rgba(40,8,30,0.85))", // it leaks into the room
    ].join(" "),
    // a slow, organic breathing so it never reads as a clean prop
    animationName: "shadowobj-breathe",
    animationDuration: "5.2s",
    animationIterationCount: "infinite",
    animationTimingFunction: "ease-in-out",
    willChange: "transform, filter",
  };
}

// Keyframes to register once (App.css is the permanent home; this is
// here so the module is self-contained for testing / drop-in). If you
// prefer, lift `SHADOW_OBJ_KEYFRAMES` into App.css and delete the
// injector below — it no-ops if the style tag already exists.
export const SHADOW_OBJ_KEYFRAMES = `
@keyframes shadowobj-breathe {
  0%   { transform: scale(1.00) translateY(0px);   filter-opacity: 1; }
  50%  { transform: scale(1.035) translateY(-1.5px); }
  100% { transform: scale(1.00) translateY(0px); }
}`;

export function ensureShadowKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById("hoc-shadowobj-keyframes")) return;
  const tag = document.createElement("style");
  tag.id = "hoc-shadowobj-keyframes";
  tag.textContent = SHADOW_OBJ_KEYFRAMES;
  document.head.appendChild(tag);
}
